import { useNavigate, useLocation } from 'react-router-dom';
import { useUserStore } from '../../lib/userStore';
import { useLeagueStore } from '../../lib/leagueStore';
import { useLanguageStore } from '../../lib/languageStore';
import { useThemeStore } from '../../lib/themeStore';
import { translate } from '../../lib/translations';
import {
  Users,
  Trophy,
  UserCog,
  UserPlus,
  MessageSquare,
  ArrowLeftRight,
  AlertCircle,
  Settings,
  Shield,
  Lightbulb,
  Gavel,
  Search,
  BookOpen,
  Home,
  History,
  HelpCircle,
  Eye,
  FileText,
  Menu,
  X,
  BarChart3,
  Filter,
  AlertTriangle,
  Hammer,
  LayoutList,
  Image as ImageIcon,
  Activity,
} from 'lucide-react';
import { useState, useEffect } from 'react';
import { supabase } from '../../lib/supabase';
import { useLeagueMode } from '../../lib/hooks/useLeagueMode';
import toast from 'react-hot-toast';

interface SidebarProps {
  isOpen: boolean;
  onToggle: () => void;
}

export function Sidebar({ isOpen, onToggle }: SidebarProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const currentUserId = useUserStore((state) => state.user_id);
  const isBpmSession = useUserStore((state) => state.is_bpm_session);
  const isTslhSession = useUserStore((state) => state.is_tslh_session);
  const selectedLeague = useLeagueStore((state) => state.selectedLeague);
  const { language } = useLanguageStore();
  const { theme } = useThemeStore();

  const [pendingTrades, setPendingTrades] = useState(0);
  const [pendingJoinRequests, setPendingJoinRequests] = useState(0);
  const [isCommissioner, setIsCommissioner] = useState(false);
  const [hasAlignmentIssue] = useState(false); // TODO: Implement alignment check for current user
  const [hasLeagueViolations, setHasLeagueViolations] = useState(false);
  const [isDraftActive, setIsDraftActive] = useState(false);
  const [nhlTeamAbbrev, setNhlTeamAbbrev] = useState<string | null>(null);
  const { isStandardFlexible, isBuildPhase } = useLeagueMode();
  const isBuildAlert = isStandardFlexible && isBuildPhase;

  useEffect(() => {
    if (!selectedLeague || !currentUserId) return;

    // Check commissioner status
    const checkCommissionerStatus = async () => {
      const { data } = await supabase
        .from('leagues')
        .select('admin_user_id, admin_user_id2')
        .eq('league_id', selectedLeague.id)
        .single();

      if (data) {
        const isMain = data.admin_user_id === Number(currentUserId);
        const isSecond = data.admin_user_id2 === Number(currentUserId);
        setIsCommissioner(isMain || isSecond);
      }
    };

    // Check pending trades
    const checkPendingTrades = async () => {
      const { data } = await supabase
        .from('gettradeshistory_players_and_users')
        .select('th_id')
        .eq('leagueid', selectedLeague.id)
        .eq('receiver_user_id', currentUserId)
        .eq('is_accepted', false)
        .eq('is_refused', false);

      setPendingTrades(data?.length || 0);
    };

    // Check pending join requests
    const checkPendingJoinRequests = async () => {
      const { data } = await supabase
        .from('league_invitation')
        .select('*')
        .eq('league_id', selectedLeague.id);

      setPendingJoinRequests(data?.length || 0);
    };

    // Check for league-wide roster violations
    const checkLeagueViolations = async () => {
      // Pas de validation d'alignement pour les ligues standard_flexible
      if (isStandardFlexible) {
        setHasLeagueViolations(false);
        return;
      }
      try {
        // Récupérer les paramètres de la ligue
        const { data: settings, error: settingsError } = await supabase
          .from('league_settings')
          .select('salary_cap, max_salary_cap, salary_cap_reserve, max_salary_cap_reserve, number_foward, number_def, number_goalie, number_reserve')
          .eq('league_id', selectedLeague.id)
          .single();

        if (settingsError) {
          console.error('Error fetching league settings:', settingsError);
          return;
        }

        // Récupérer tous les utilisateurs de la ligue
        const { data: leagueUsers, error: usersError } = await supabase
          .from('leagues_users')
          .select('user_id')
          .eq('league_id', selectedLeague.id);

        if (usersError) {
          console.error('Error fetching league users:', usersError);
          return;
        }

        let hasViolations = false;

        // Vérifier rapidement s'il y a au moins une violation
        for (const leagueUser of leagueUsers || []) {
          // Vérifier le nombre de joueurs par position
          const { data: playerCounts } = await supabase
            .from('get_number_type_player_per_team')
            .select('number_of_fowards, number_of_defenders')
            .eq('league_id', selectedLeague.id)
            .eq('user_id', leagueUser.user_id)
            .single();

          if (playerCounts) {
            const maxForwards = settings?.number_foward || 9;
            const actualForwards = playerCounts.number_of_fowards || 0;
            if (actualForwards > maxForwards || actualForwards < maxForwards) {
              hasViolations = true;
              break;
            }

            const maxDefenders = settings?.number_def || 6;
            const actualDefenders = playerCounts.number_of_defenders || 0;
            if (actualDefenders > maxDefenders || actualDefenders < maxDefenders) {
              hasViolations = true;
              break;
            }
          }

          // Vérifier le nombre de gardiens
          const { data: goalieCounts } = await supabase
            .from('get_number_type_goalie_per_team')
            .select('number_of_goalie')
            .eq('league_id', selectedLeague.id)
            .eq('user_id', leagueUser.user_id)
            .single();

          if (goalieCounts) {
            const maxGoalies = settings?.number_goalie || 3;
            const actualGoalies = goalieCounts.number_of_goalie || 0;
            if (actualGoalies > maxGoalies || actualGoalies < maxGoalies) {
              hasViolations = true;
              break;
            }
          }
        }

        setHasLeagueViolations(hasViolations);
      } catch (error) {
        console.error('Error checking league violations:', error);
      }
    };

    checkCommissionerStatus();
    checkPendingTrades();
    checkPendingJoinRequests();
    checkLeagueViolations();

    // Charger le logo NHL de l'équipe de l'utilisateur dans cette ligue
    const checkTeamLogo = async () => {
      setNhlTeamAbbrev(null);
      const { data } = await supabase
        .from('leagues_users')
        .select('nhl_team_abbrev')
        .eq('league_id', selectedLeague.id)
        .eq('user_id', Number(currentUserId))
        .single();
      if (data) {
        setNhlTeamAbbrev((data as { nhl_team_abbrev?: string | null }).nhl_team_abbrev ?? null);
      }
    };
    checkTeamLogo();

    // Real-time subscriptions
    // TEMP DEBUG iPhone WebSocket: subscription 'trades-changes' désactivée temporairement (code conservé, aucun .subscribe() appelé)
    // const tradesSubscription = supabase
    //   .channel('trades-changes')
    //   .on('postgres_changes', {
    //     event: '*',
    //     schema: 'public',
    //     table: 'trades_history',
    //     filter: `receiver_user_id=eq.${currentUserId}`,
    //   }, () => checkPendingTrades())
    //   .subscribe();
    const tradesSubscription: { unsubscribe: () => void } | null = null; // TEMP DEBUG iPhone WebSocket

    // TEMP DEBUG iPhone WebSocket: subscription 'join-requests-changes' désactivée temporairement (code conservé, aucun .subscribe() appelé)
    // const joinRequestsSubscription = supabase
    //   .channel('join-requests-changes')
    //   .on('postgres_changes', {
    //     event: '*',
    //     schema: 'public',
    //     table: 'league_invitation',
    //     filter: `league_id=eq.${selectedLeague.id}`,
    //   }, () => checkPendingJoinRequests())
    //   .subscribe();
    const joinRequestsSubscription: { unsubscribe: () => void } | null = null; // TEMP DEBUG iPhone WebSocket

    return () => {
      // TEMP DEBUG iPhone WebSocket: cleanup protégé (les subscriptions peuvent être null)
      tradesSubscription?.unsubscribe?.();
      joinRequestsSubscription?.unsubscribe?.();
    };
  }, [selectedLeague, currentUserId]);

  useEffect(() => {
    if (!selectedLeague) return;

    let isMounted = true;

    const checkDraftStatus = async () => {
      try {
        const { data, error } = await supabase
          .from('league_settings')
          .select('draftMode')
          .eq('league_id', selectedLeague.id)
          .single();

        if (error && (error as any).code !== 'PGRST116') {
          console.error('Error checking draft status:', error);
          return;
        }

        if (isMounted) {
          setIsDraftActive(!!data?.draftMode);
        }
      } catch (err) {
        console.error('Error checking draft status:', err);
        if (isMounted) {
          setIsDraftActive(false);
        }
      }
    };

    checkDraftStatus();

    // TEMP DEBUG iPhone WebSocket: subscription 'league-settings-draft-sidebar' désactivée temporairement (code conservé, aucun .subscribe() appelé)
    // const draftStatusSubscription = supabase
    //   .channel('league-settings-draft-sidebar')
    //   .on(
    //     'postgres_changes',
    //     { event: '*', schema: 'public', table: 'league_settings', filter: `league_id=eq.${selectedLeague.id}` },
    //     (payload) => {
    //       const newDraft = (payload as any)?.new?.draftMode;
    //       if (typeof newDraft === 'boolean') {
    //         setIsDraftActive(newDraft);
    //       }
    //     }
    //   )
    //   .subscribe();
    const draftStatusSubscription: { unsubscribe: () => void } | null = null; // TEMP DEBUG iPhone WebSocket

    return () => {
      isMounted = false;
      // TEMP DEBUG iPhone WebSocket: cleanup protégé (la subscription peut être null)
      draftStatusSubscription?.unsubscribe?.();
    };
  }, [selectedLeague]);

  const menuItems = [
    {
      icon: Home,
      title: 'Accueil',
      path: '/my-office',
    },
    {
      icon: Trophy,
      title: translate('standings', language),
      path: '/standings',
    },
    // Placer Mon équipe immédiatement APRÈS Classement
    {
      icon: Users,
      title: translate('my.team', language),
      path: '/my-teams',
      badge: hasAlignmentIssue ? '!' : undefined,
    },
    {
      icon: BookOpen,
      title: 'Guide utilisateur',
      path: '/guide',
    },
    {
      icon: BarChart3,
      title: language === 'fr' ? 'Choisir sa ligue' : 'Choose league',
      path: '/dashboard',
    },
    {
      icon: AlertTriangle,
      title: language === 'fr' ? 'Validation Alignements' : 'Roster Validation',
      path: '/roster-validation',
      badge: hasLeagueViolations ? '!' : undefined,
      showDanger: hasLeagueViolations,
    },
    {
      icon: LayoutList,
      title: 'Lignes NHL',
      path: '/line-combinations',
    },
    {
      icon: Activity,
      title: 'NHL Edge™ Stats',
      path: '/nhl-edge-hub',
    },
    {
      icon: Filter,
      title: 'Recherche Avancée',
      path: '/advanced-player-search',
    },
    {
      icon: Search,
      title: 'Intra-ligue',
      path: '/other-teams',
    },
    {
      icon: ArrowLeftRight,
      title: translate('trade.center', language),
      path: '/trade-player',
    },
    {
      icon: UserPlus,
      title: translate('free.agents', language),
      path: '/free-agents',
    },
    {
      icon: Hammer,
      title: 'Bâtir mon équipe',
      path: '/build-my-team',
    },
    {
      icon: BookOpen,
      title: 'Règles',
      path: '/league-rules',
    },
    {
      icon: Eye,
      title: 'Watchlist',
      path: '/watchlist',
    },
    {
      icon: Gavel,
      title: translate('draft.title', language),
      path: '/draft',
    },
    {
      icon: AlertCircle,
      title: translate('waivers', language),
      path: '/waivers',
      disabled: true,
    },
    {
      icon: FileText,
      title: 'Trading Bloc',
      path: '/trading-bloc',
    },
    {
      icon: History,
      title: 'Historique',
      path: '/league-history',
    },
    {
      icon: MessageSquare,
      title: translate('messages', language),
      path: '/messages',
      badge: pendingTrades + (isCommissioner ? pendingJoinRequests : 0),
      showDanger: isCommissioner && hasLeagueViolations,
    },
    {
      icon: HelpCircle,
      title: 'FAQ',
      path: '/faq',
    },
    {
      icon: Settings,
      title: translate('league.settings', language),
      path: '/settings',
    },
    {
      icon: Shield,
      title: translate('protection.list', language),
      path: '/protection-list',
    },
    {
      icon: Lightbulb,
      title: 'Recommandations',
      path: '/recommendations',
    },
    {
      icon: UserCog,
      title: 'Mon profil',
      path: '/update-username',
    },
    {
      icon: ImageIcon,
      title: 'Logo de mon équipe',
      path: '/team-profile',
      badge: !nhlTeamAbbrev ? '!' : undefined,
    },
    {
      icon: Lightbulb,
      title: 'Tutoriels',
      path: '/tutoriels',
    },
    ...(Number(currentUserId) === 3 ? [{
      icon: BarChart3,
      title: 'Statistiques',
      path: '/stats',
    }] : []),
    {
      icon: FileText,
      title: 'Paiement',
      path: '/paiement',
    },
  ];

  const draftMenuItem = menuItems.find((item) => item.path === '/draft');
  const buildMyTeamMenuItem = menuItems.find((item) => item.path === '/build-my-team');

  let orderedMenuItems = isDraftActive && draftMenuItem
    ? [draftMenuItem, ...menuItems.filter((item) => item.path !== '/draft')]
    : menuItems;
  // When build phase is active, always put Bâtir mon équipe first
  if (isBuildAlert && buildMyTeamMenuItem) {
    orderedMenuItems = [buildMyTeamMenuItem, ...orderedMenuItems.filter((item) => item.path !== '/build-my-team')];
  }
  // Masquer la validation d'alignement pour les ligues standard_flexible
  if (isStandardFlexible) {
    orderedMenuItems = orderedMenuItems.filter((item) => item.path !== '/roster-validation');
  }
  // En phase de construction (standard flexible, équipe incomplète) : masquer Mon équipe et Agents libres
  if (isBuildPhase) {
    orderedMenuItems = orderedMenuItems.filter((item) => item.path !== '/my-teams' && item.path !== '/free-agents');
  }

  // Mode BPM : restreindre aux pages essentielles
  const BPM_ALLOWED_PATHS = ['/my-office', '/my-teams', '/other-teams', '/standings', '/team-profile', '/build-my-team', '/free-agents', '/line-combinations'];
  if (isBpmSession) {
    orderedMenuItems = orderedMenuItems.filter((item) => BPM_ALLOWED_PATHS.includes(item.path));
  }
  // Mode TSLH : restreindre aux pages TSLH (parallèle à BPM, liste distincte)
  const TSLH_SIDEBAR_PATHS = ['/my-office', '/build-my-team', '/my-teams', '/free-agents', '/other-teams', '/standings', '/team-profile', '/league-rules', '/line-combinations'];
  if (isTslhSession) {
    orderedMenuItems = orderedMenuItems.filter((item) => TSLH_SIDEBAR_PATHS.includes(item.path));
  }
  // Mettre Messages en premier quand il y a des offres d'échange ou demandes en attente
  const messagesMenuItem = menuItems.find((item) => item.path === '/messages');
  if ((pendingTrades > 0 || (isCommissioner && pendingJoinRequests > 0)) && messagesMenuItem) {
    orderedMenuItems = [messagesMenuItem, ...orderedMenuItems.filter((item) => item.path !== '/messages')];
  }

  // Mettre Logo en premier (avec badge !) si l'utilisateur n'a pas encore choisi de logo
  const logoMenuItem = menuItems.find((item) => item.path === '/team-profile');
  if (selectedLeague && !nhlTeamAbbrev && logoMenuItem) {
    orderedMenuItems = [logoMenuItem, ...orderedMenuItems.filter((item) => item.path !== '/team-profile')];
  }

  const handleNavigation = (path: string, disabled?: boolean) => {
    if (disabled) {
      toast.error(translate('coming.soon', language));
      return;
    }
    navigate(path);
  };

  const isActive = (path: string) => location.pathname === path;

  return (
    <>
      {/* Overlay pour mobile/tablette */}
      {isOpen && (
        <div
          className="sidebar-overlay"
          onClick={onToggle}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            zIndex: 998,
            display: window.innerWidth < 1200 ? 'block' : 'none',
          }}
        />
      )}

      {/* Sidebar */}
      <div
        className={`sidebar ${isOpen ? 'open' : 'closed'}`}
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          height: '100dvh',
          width: isOpen ? '280px' : '0',
          backgroundColor: theme === 'dark' 
            ? 'rgba(15, 23, 42, 0.95)' 
            : 'rgba(255, 255, 255, 0.95)',
          backdropFilter: 'blur(10px)',
          borderRight: isOpen 
            ? (theme === 'dark' 
              ? '1px solid rgba(59, 130, 246, 0.2)' 
              : '1px solid rgba(203, 213, 225, 0.5)')
            : 'none',
          overflowY: 'auto',
          overflowX: 'hidden',
          transition: 'width 0.3s ease, box-shadow 0.3s ease',
          zIndex: 999,
          boxShadow: isOpen 
            ? (theme === 'dark'
              ? '2px 0 20px rgba(0, 0, 0, 0.5)'
              : '2px 0 8px rgba(0, 0, 0, 0.1)')
            : 'none',
        }}
      >
        {isOpen && (
          <div className="sidebar-content p-3">
            {/* Header avec bouton fermer */}
            <div 
              className="d-flex justify-content-between align-items-center mb-4 pb-3"
              style={{
                borderBottom: theme === 'dark'
                  ? '1px solid rgba(59, 130, 246, 0.2)'
                  : '1px solid rgba(203, 213, 225, 0.5)',
              }}
            >
              <h5 
                className="mb-0 fw-bold"
                style={{
                  color: theme === 'dark' ? '#ffffff' : '#0d6efd',
                }}
              >
                🏒 Navigation
              </h5>
              <button
                className="btn btn-sm btn-ghost"
                onClick={onToggle}
                style={{
                  border: 'none',
                  background: 'transparent',
                  cursor: 'pointer',
                  color: theme === 'dark' ? '#ffffff' : '#495057',
                }}
              >
                <X size={20} />
              </button>
            </div>

            {/* Menu items */}
            <nav>
              {orderedMenuItems.map((item, index) => {
                const Icon = item.icon;
                const active = isActive(item.path);
                const isDraftItem = item.path === '/draft' && isDraftActive;

                return (
                  <button
                    key={index}
                    onClick={() => handleNavigation(item.path, item.disabled)}
                    disabled={item.disabled}
                    className={`sidebar-item ${active ? 'active' : ''}`}
                    style={(() => {
                      const isBuildItem = isBuildAlert && item.path === '/build-my-team';
                      const isHighlightItem = isDraftItem || isBuildItem;
                      return {
                        display: 'flex',
                        alignItems: 'center',
                        width: '100%',
                        padding: '12px 16px',
                        marginBottom: '4px',
                        border: 'none',
                        borderRadius: '8px',
                        background: active
                          ? (theme === 'dark'
                            ? 'linear-gradient(135deg, rgba(59, 130, 246, 0.2) 0%, rgba(139, 92, 246, 0.2) 100%)'
                            : 'linear-gradient(135deg, rgba(59, 130, 246, 0.15) 0%, rgba(139, 92, 246, 0.15) 100%)')
                          : isHighlightItem
                            ? 'rgba(245, 158, 11, 0.2)'
                          : 'transparent',
                        color: active
                          ? (isHighlightItem ? '#fbbf24' : (theme === 'dark' ? '#ffffff' : '#0d6efd'))
                          : (isHighlightItem ? '#fbbf24' : (theme === 'dark' ? '#ffffff' : '#495057')),
                        cursor: item.disabled ? 'not-allowed' : 'pointer',
                        opacity: item.disabled ? 0.5 : 1,
                        textAlign: 'left' as const,
                        transition: 'all 0.2s ease',
                        fontWeight: (active || isBuildItem) ? 600 : 400,
                        borderLeft: active
                          ? (theme === 'dark' ? '3px solid #ffffff' : '3px solid #0d6efd')
                          : (isHighlightItem ? '3px solid rgba(245, 158, 11, 0.9)' : '3px solid transparent'),
                      };
                    })()}
                    onMouseEnter={(e) => {
                      const isBuildItem = isBuildAlert && item.path === '/build-my-team';
                      const isHighlightItem = isDraftItem || isBuildItem;
                      if (!item.disabled && !active) {
                        e.currentTarget.style.backgroundColor = isHighlightItem
                          ? 'rgba(245, 158, 11, 0.25)'
                          : (theme === 'dark'
                            ? 'rgba(59, 130, 246, 0.1)'
                            : 'rgba(0, 0, 0, 0.05)');
                      }
                    }}
                    onMouseLeave={(e) => {
                      const isBuildItem = isBuildAlert && item.path === '/build-my-team';
                      const isHighlightItem = isDraftItem || isBuildItem;
                      if (!active) {
                        e.currentTarget.style.backgroundColor = isHighlightItem
                          ? 'rgba(245, 158, 11, 0.2)'
                          : 'transparent';
                      }
                    }}
                  >
                    <Icon size={18} className="me-3" />
                    <span className="flex-grow-1">{item.title}</span>
                    {isBuildAlert && item.path === '/build-my-team' && (
                      <span className="badge rounded-pill ms-1" style={{ backgroundColor: '#FACC15', color: '#1f2937', fontSize: '0.65rem' }}>!</span>
                    )}
                    
                    {/* Badges */}
                    {item.badge && item.badge !== 0 && (
                      <span
                        className="badge rounded-pill"
                        style={{
                          backgroundColor: item.showDanger ? '#dc3545' : '#0d6efd',
                          color: 'white',
                          fontSize: '0.75rem',
                          minWidth: '20px',
                          height: '20px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        {item.badge}
                      </span>
                    )}
                  </button>
                );
              })}
            </nav>
          </div>
        )}
      </div>

      {/* Toggle button (visible quand sidebar fermée ou sur mobile) */}
      {!isOpen && (
        <button
          className="sidebar-toggle-btn"
          onClick={onToggle}
          style={{
            position: 'fixed',
            top: '20px',
            left: '20px',
            zIndex: 1000,
            width: '40px',
            height: '40px',
            borderRadius: '8px',
            border: '1px solid var(--bs-border-color)',
            backgroundColor: 'var(--bs-body-bg)',
            color: 'var(--bs-body-color)',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
            transition: 'all 0.2s ease',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = 'var(--bs-primary)';
            e.currentTarget.style.color = 'white';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = 'var(--bs-body-bg)';
            e.currentTarget.style.color = 'var(--bs-body-color)';
          }}
        >
          <Menu size={20} />
        </button>
      )}
    </>
  );
}
